<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">财富客户</h1>
            <p class="list-page-description">以客户为主体查看合同、批次、流水和申请记录。</p>
          </div>
          <div class="list-page-heading-actions">
            <button type="button" class="btn-secondary inline-flex items-center gap-2" :disabled="statisticsLoading" @click="loadStatistics">
              <svg class="h-4 w-4" :class="{ 'animate-spin': statisticsLoading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              刷新摘要
            </button>
            <button v-permission="'wealth:customer:create'" type="button" class="btn-primary inline-flex items-center gap-2" @click="openCreate">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              新建客户
            </button>
          </div>
        </div>
      </div>
    </template>

    <div class="mx-6 mb-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <WealthMetricCard title="财富客户数" :value="overview.customerCount || 0" description="授权范围内客户" />
      <WealthMetricCard title="当前总本金" :value="`¥${formatMoney(overview.currentPrincipalAmount)}`" description="有效及已到期未处理批次" />
      <WealthMetricCard title="即将到期" :value="`${overview.upcomingMaturityBatchCount || 0} 批`" :description="`金额 ¥${formatMoney(overview.upcomingMaturityAmount)}`" tone="amber" />
      <WealthMetricCard title="已到期未处理" :value="`${overview.maturedUnhandledBatchCount || 0} 批`" :description="`金额 ¥${formatMoney(overview.maturedUnhandledAmount)}`" tone="red" />
    </div>

    <div class="list-page-table-panel mx-6">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="refreshCustomerListAndStatistics"
      >
        <div class="filter-bar-field is-wide relative">
          <input v-model="filters.keyword" class="form-search-input form-control-sm w-full" placeholder="姓名/手机号/证件号" @keyup.enter="handleSearch">
          <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <el-select v-model="filters.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部状态" value="" />
          <el-option v-for="item in customerStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-select v-model="filters.riskLevel" placeholder="请选择风险等级" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部风险等级" value="" />
          <el-option v-for="item in riskLevelOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <UserSelector v-model="filters.ownerUserId" class="selector-compact filter-bar-field" :multiple="false" placeholder="选择负责人" />
        <DeptSelector v-model="filters.ownerDept" class="selector-compact filter-bar-field" :multiple="false" placeholder="选择部门" />
        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">客户</th>
                <th class="data-table-th text-left">联系方式</th>
                <th class="data-table-th text-left">证件</th>
                <th class="data-table-th text-left">负责人</th>
                <th class="data-table-th text-left">风险等级</th>
                <th class="data-table-th text-left">状态</th>
                <th class="data-table-th text-left">创建时间</th>
                <th class="data-table-th text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td v-for="m in 8" :key="m" class="data-table-td">
                    <div class="h-4 bg-slate-200 rounded w-24"/>
                  </td>
                </tr>
              </template>
              <tr v-else-if="rows.length === 0">
                <td colspan="8" class="data-table-empty">暂无财富客户</td>
              </tr>
              <tr v-for="row in rows" v-else :key="row.id" class="data-table-row">
                <td class="data-table-td">
                  <div class="font-medium text-slate-900">{{ row.name }}</div>
                  <div class="font-mono text-xs text-slate-500">{{ row.customerNo || '-' }}</div>
                </td>
                <td class="data-table-td">{{ maskPhone(row.mobile) }}</td>
                <td class="data-table-td">
                  <div>{{ findOptionLabel(idTypeOptions, row.idType) }}</div>
                  <div class="text-xs text-slate-500">{{ maskIdNo(row.idNo) }}</div>
                </td>
                <td class="data-table-td">
                  <div>{{ row.ownerUserName || row.ownerUserId || '-' }}</div>
                  <div class="text-xs text-slate-500">{{ row.ownerDeptName || row.ownerDeptId || '-' }}</div>
                </td>
                <td class="data-table-td">
                  <div class="flex flex-wrap gap-1.5">
                    <WealthStatusBadge type="risk" :value="row.riskLevel" />
                  </div>
                </td>
                <td class="data-table-td"><WealthStatusBadge type="customer" :value="row.status" show-dot /></td>
                <td class="data-table-td text-xs text-slate-600">{{ formatDateTime(row.createTime) }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button class="table-action-button is-primary" title="详情" aria-label="详情" @click="openDetail(row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <el-dropdown trigger="click" :hide-on-click="true">
                      <button class="table-action-button is-neutral" title="更多" aria-label="更多操作" @click.stop>
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm6 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm6 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
                        </svg>
                      </button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <el-dropdown-item v-if="row.status === 'ACTIVE'" v-permission="'wealth:fund:invest'" @click="openInvest(row)">注资</el-dropdown-item>
                          <el-dropdown-item v-permission="'wealth:customer:update'" @click="openEdit(row)">编辑</el-dropdown-item>
                          <el-dropdown-item v-if="row.status === 'ACTIVE'" v-permission="'wealth:customer:disable'" divided @click="changeStatus(row, 'disable')">
                            <span class="text-red-600">停用</span>
                          </el-dropdown-item>
                          <el-dropdown-item v-else v-permission="'wealth:customer:update'" divided @click="changeStatus(row, 'enable')">启用</el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="PAGE_SIZE_OPTIONS"
        @page-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <WealthCustomerFormModal v-model="formVisible" :customer="editingCustomer" @saved="afterCustomerSaved" />
    <WealthOperationApplyModal v-model="applyVisible" :operation-type="applyType" :preset="applyPreset" @submitted="afterApplySubmitted" />

    <WealthDetailDrawer v-model="detailVisible" title="财富客户详情" :subtitle="selectedCustomer?.customerNo">
      <div v-if="selectedCustomer" class="space-y-5">
        <section class="rounded-lg border border-slate-200 bg-white p-4">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 class="text-base font-semibold text-slate-900">{{ selectedCustomer.name }}</h3>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                <WealthStatusBadge type="customer" :value="selectedCustomer.status" show-dot />
                <WealthStatusBadge type="risk" :value="selectedCustomer.riskLevel" />
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <button v-permission="'wealth:fund:invest'" type="button" class="btn-primary" @click="openInvest(selectedCustomer)">注资</button>
              <button v-permission="'wealth:customer:update'" type="button" class="btn-secondary" @click="openEdit(selectedCustomer)">编辑客户</button>
            </div>
          </div>
        </section>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div class="rounded-lg border border-slate-200 p-4">
            <div class="text-xs text-slate-500">当前本金</div>
            <div class="mt-2 text-lg money-text text-slate-900">¥{{ formatMoney(account.currentPrincipalAmount) }}</div>
          </div>
          <div class="rounded-lg border border-slate-200 p-4">
            <div class="text-xs text-slate-500">累计注资</div>
            <div class="mt-2 text-lg money-text text-slate-900">¥{{ formatMoney(account.totalInvestAmount) }}</div>
          </div>
          <div class="rounded-lg border border-slate-200 p-4">
            <div class="text-xs text-slate-500">累计增资</div>
            <div class="mt-2 text-lg money-text text-slate-900">¥{{ formatMoney(account.totalIncreaseAmount) }}</div>
          </div>
          <div class="rounded-lg border border-slate-200 p-4">
            <div class="text-xs text-slate-500">累计撤资</div>
            <div class="mt-2 text-lg money-text text-slate-900">¥{{ formatMoney(account.totalWithdrawAmount) }}</div>
          </div>
        </div>

        <div class="border-b border-slate-200">
          <nav class="-mb-px flex flex-wrap gap-5 text-sm">
            <button
              v-for="tab in detailTabs"
              :key="tab.value"
              type="button"
              class="border-b-2 px-1 py-3 font-medium transition-colors"
              :class="activeTab === tab.value ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'"
              @click="activeTab = tab.value"
            >
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <section v-show="activeTab === 'overview'" class="space-y-5">
          <div class="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-4">
            <FieldBlock label="手机号" :value="maskPhone(selectedCustomer.mobile)" />
            <FieldBlock label="证件" :value="`${findOptionLabel(idTypeOptions, selectedCustomer.idType)} ${maskIdNo(selectedCustomer.idNo)}`" />
            <template v-if="selectedCustomer.linkedCrmCustomerId">
              <FieldBlock label="关联 CRM 客户" :value="linkedCrmCustomerDisplay" />
            </template>
            <FieldBlock label="负责人" :value="selectedCustomer.ownerUserName || selectedCustomer.ownerUserId || '-'" />
            <FieldBlock label="所属部门" :value="selectedCustomer.ownerDeptName || selectedCustomer.ownerDeptId || '-'" />
            <FieldBlock label="地址" :value="selectedCustomer.address || '-'" />
            <FieldBlock label="紧急联系人" :value="selectedCustomer.emergencyContactName || '-'" />
            <FieldBlock label="紧急联系电话" :value="maskPhone(selectedCustomer.emergencyContactMobile)" />
            <FieldBlock label="备注" :value="selectedCustomer.remark || '-'" />
          </div>
        </section>

        <section v-show="activeTab === 'contracts'" class="space-y-4">
          <div class="rounded-lg border border-slate-200 bg-white">
            <div class="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <h4 class="text-sm font-semibold text-slate-900">客户合同</h4>
              <button type="button" class="btn-secondary" :disabled="contractsLoading" @click="loadContracts">刷新</button>
            </div>
            <div class="data-table-shell border-0 shadow-none wealth-contract-table-layout">
              <div class="data-table-scroll wealth-contract-table-scroll">
                <table class="data-table text-sm">
                  <thead class="data-table-head">
                    <tr>
                      <th class="data-table-th text-left">合同</th>
                      <th class="data-table-th text-right">当前本金</th>
                      <th class="data-table-th text-left">注资日期</th>
                      <th class="data-table-th text-left">状态</th>
                      <th class="data-table-th text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="contractsLoading"><td colspan="5" class="data-table-empty">加载中...</td></tr>
                    <tr v-else-if="contracts.length === 0"><td colspan="5" class="data-table-empty">暂无合同</td></tr>
                    <template v-for="item in contracts" v-else :key="item.id">
                      <tr class="data-table-row">
                        <td class="data-table-td">
                          <button type="button" class="text-left font-medium text-primary-600 hover:text-primary-700" @click="toggleContract(item)">
                            {{ item.contractName }}
                          </button>
                          <div class="font-mono text-xs text-slate-500">{{ item.contractNo }}</div>
                          <div v-if="contractOngoingOperationMap[item.id]?.length" class="contract-row-ongoing-approval mt-2 flex flex-wrap gap-1.5">
                            <div
                              v-for="operation in contractOngoingOperationMap[item.id]"
                              :key="`row-${operation.applyId}`"
                              class="inline-flex items-center gap-1"
                            >
                              <button
                                type="button"
                                class="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
                                @click.stop="openOngoingOperationDetail(operation)"
                              >
                                {{ formatOngoingOperationSummary(operation) }}
                              </button>
                              <button
                                v-if="operation.apply?.approvalInstanceId"
                                type="button"
                                class="table-action-button is-purple"
                                title="查看审批详情" aria-label="查看审批详情"
                                @click.stop="openOngoingOperationApprovalDetail(operation)"
                              >
                                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </td>
                        <td class="data-table-td is-right money-text">¥{{ formatMoney(item.currentPrincipalAmount) }}</td>
                        <td class="data-table-td">{{ formatDate(item.signDate) }}</td>
                        <td class="data-table-td"><WealthStatusBadge type="contract" :value="item.status" show-dot /></td>
                        <td class="data-table-td is-center">
                          <div class="flex justify-center gap-1">
                            <button v-if="item.status === 'ACTIVE'" v-permission="'wealth:fund:increase'" class="table-action-button is-success" title="增资" aria-label="增资" @click="openContractApply('INCREASE', item)">
                              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/></svg>
                            </button>
                            <button v-if="item.status === 'ACTIVE'" v-permission="'wealth:fund:decrease'" class="table-action-button is-warning" title="减资" aria-label="减资" @click="openContractApply('DECREASE', item)">
                              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181"/></svg>
                            </button>
                            <button v-if="item.status === 'ACTIVE'" v-permission="'wealth:fund:withdraw'" class="table-action-button is-danger" title="撤资" aria-label="撤资" @click="openContractApply('WITHDRAW', item)">
                              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="expandedContractId === item.id">
                        <td colspan="5" class="bg-slate-50 p-4">
                          <div class="space-y-4">
                            <WealthBatchPanel
                              :contract-id="item.id"
                              title="合同批次"
                              :show-mode-switch="false"
                              :ongoing-operations="batchOngoingOperationMap"
                              @view-operation="openOngoingOperationDetail"
                              @view-approval="openOngoingOperationApprovalDetail"
                            >
                              <template #actions="{ row }">
                                <div class="flex justify-center gap-1">
                                  <button v-permission="'wealth:ledger:view'" class="table-action-button is-primary" title="批次流水" aria-label="批次流水" @click="openBatchLedgerDetail(row)">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                                  </button>
                                  <button v-if="item.status === 'ACTIVE'" v-permission="'wealth:fund:rollover'" class="table-action-button is-indigo" title="续期" aria-label="续期" @click="openRollover(item, row)">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 3h5v5"/>
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 8a9 9 0 00-15.5-3.5"/>
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 21H3v-5"/>
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 16a9 9 0 0015.5 3.5"/>
                                    </svg>
                                  </button>
                                </div>
                              </template>
                            </WealthBatchPanel>
                            <WealthAttachmentPanel title="合同附件" biz-type="CONTRACT" :biz-id="item.id" :can-manage="true" />
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
            <AppPagination
              v-if="contractPagination.total > 0"
              class="pagination-bridge px-4 pb-4 wealth-contract-pagination-sticky"
              :pagination="contractPagination"
              :page-sizes="PAGE_SIZE_OPTIONS"
              @page-change="handleContractPageChange"
              @size-change="handleContractSizeChange"
            />
          </div>
        </section>

        <section v-show="activeTab === 'ledgers'" class="space-y-4">
          <div class="rounded-lg border border-slate-200 bg-white">
            <div class="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <h4 class="text-sm font-semibold text-slate-900">客户流水</h4>
              <div class="flex flex-wrap items-end gap-2">
                <el-select v-model="ledgerFilters.operationType" placeholder="请选择类型" class="form-select form-control-sm w-60">
                  <el-option label="全部类型" value="" />
                  <el-option v-for="item in operationTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
                <button type="button" class="btn-secondary" :disabled="ledgersLoading" @click="loadLedgers">搜索</button>
              </div>
            </div>
            <div class="data-table-shell border-0 shadow-none">
              <div class="data-table-scroll">
                <table class="data-table text-sm">
                  <thead class="data-table-head">
                    <tr>
                      <th class="data-table-th text-left">流水</th>
                      <th class="data-table-th text-left">类型</th>
                      <th class="data-table-th text-left">方向</th>
                      <th class="data-table-th text-left">合同/批次</th>
                      <th class="data-table-th text-right">金额</th>
                      <th class="data-table-th text-right">账户本金前后</th>
                      <th class="data-table-th text-left">时间</th>
                      <th class="data-table-th text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="ledgersLoading">
                      <tr v-for="n in 3" :key="n" class="animate-pulse">
                        <td v-for="m in 8" :key="m" class="data-table-td"><div class="h-4 bg-slate-200 rounded w-24"/></td>
                      </tr>
                    </template>
                    <tr v-else-if="ledgers.length === 0"><td colspan="8" class="data-table-empty">暂无流水</td></tr>
                    <tr v-for="row in ledgers" v-else :key="row.id" class="data-table-row">
                      <td class="data-table-td"><div class="font-mono text-xs">{{ row.ledgerNo }}</div><div class="font-mono text-xs text-slate-500">{{ row.accountingSerialNo }}</div></td>
                      <td class="data-table-td"><WealthStatusBadge type="operation" :value="row.operationType" /></td>
                      <td class="data-table-td"><WealthStatusBadge type="direction" :value="row.direction" /></td>
                      <td class="data-table-td"><div>合同 {{ row.contractId || '-' }}</div><div class="text-xs text-slate-500">批次 {{ row.batchId || '-' }}</div></td>
                      <td class="data-table-td is-right money-text" :class="row.direction === 'OUT' ? 'text-red-600' : 'text-emerald-600'">{{ row.direction === 'OUT' ? '-' : '+' }}¥{{ formatMoney(row.amount) }}</td>
                      <td class="data-table-td is-right">¥{{ formatMoney(row.beforeAccountPrincipal) }} → ¥{{ formatMoney(row.afterAccountPrincipal) }}</td>
                      <td class="data-table-td text-xs">{{ formatDateTime(row.createTime) }}</td>
                      <td class="data-table-td is-center">
                        <button class="table-action-button is-primary" title="明细" aria-label="明细" @click="openLedgerDetail(row)">
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <AppPagination
              v-if="ledgerPagination.total > 0"
              class="pagination-bridge px-4 pb-4"
              :pagination="ledgerPagination"
              :page-sizes="PAGE_SIZE_OPTIONS"
              @page-change="handleLedgerPageChange"
              @size-change="handleLedgerSizeChange"
            />
          </div>
        </section>

        <section v-show="activeTab === 'applies'" class="space-y-4">
          <div class="rounded-lg border border-slate-200 bg-white">
            <div class="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <h4 class="text-sm font-semibold text-slate-900">申请记录</h4>
              <div class="flex flex-wrap items-end gap-2">
                <el-select v-model="applyFilters.status" placeholder="请选择状态" class="form-select form-control-sm w-60">
                  <el-option label="全部状态" value="" />
                  <el-option v-for="item in applyStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
                <button type="button" class="btn-secondary" :disabled="appliesLoading" @click="loadApplies">搜索</button>
              </div>
            </div>
            <div class="data-table-shell border-0 shadow-none">
              <div class="data-table-scroll">
                <table class="data-table text-sm">
                  <thead class="data-table-head">
                    <tr>
                      <th class="data-table-th text-left">申请</th>
                      <th class="data-table-th text-left">类型</th>
                      <th class="data-table-th text-right">金额</th>
                      <th class="data-table-th text-left">状态</th>
                      <th class="data-table-th text-left">申请时间</th>
                      <th class="data-table-th text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="appliesLoading">
                      <tr v-for="n in 3" :key="n" class="animate-pulse">
                        <td v-for="m in 6" :key="m" class="data-table-td"><div class="h-4 bg-slate-200 rounded w-24"/></td>
                      </tr>
                    </template>
                    <tr v-else-if="applies.length === 0"><td colspan="6" class="data-table-empty">暂无申请记录</td></tr>
                    <tr v-for="row in applies" v-else :key="row.id" class="data-table-row">
                      <td class="data-table-td">
                        <div class="font-mono text-xs text-slate-900">{{ row.applyNo }}</div>
                        <div class="mt-1 flex items-center gap-2 text-xs text-slate-500">
                          <span>实例 {{ row.approvalInstanceId || '-' }}</span>
                          <button
                            v-if="row.approvalInstanceId"
                            type="button"
                            class="inline-approval-link rounded px-1 text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                            @click.stop="openApprovalDetail(row)"
                          >
                            审批详情
                          </button>
                        </div>
                      </td>
                      <td class="data-table-td"><WealthStatusBadge type="operation" :value="row.operationType" /></td>
                      <td class="data-table-td is-right">¥{{ formatMoney(row.rolloverPrincipalAmount || row.amount) }}</td>
                      <td class="data-table-td"><WealthStatusBadge type="apply" :value="row.status" show-dot /></td>
                      <td class="data-table-td text-xs">{{ formatDateTime(row.applyTime) }}</td>
                      <td class="data-table-td is-center">
                        <div class="flex items-center justify-center gap-1">
                          <button class="table-action-button is-primary" title="详情" aria-label="详情" @click="openApplyDetail(row)">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button v-if="row.approvalInstanceId" class="table-action-button is-purple" title="查看审批详情" aria-label="查看审批详情" @click="openApprovalDetail(row)">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <AppPagination
              v-if="applyPagination.total > 0"
              class="pagination-bridge px-4 pb-4"
              :pagination="applyPagination"
              :page-sizes="PAGE_SIZE_OPTIONS"
              @page-change="handleApplyPageChange"
              @size-change="handleApplySizeChange"
            />
          </div>
        </section>
      </div>
    </WealthDetailDrawer>

    <WealthDetailDrawer v-model="ledgerDetailVisible" title="流水详情" :subtitle="selectedLedger?.ledgerNo">
      <div v-if="selectedLedger" class="space-y-5">
        <section class="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-4">
          <div><div class="text-xs text-slate-500">操作类型</div><WealthStatusBadge type="operation" :value="selectedLedger.operationType" /></div>
          <div><div class="text-xs text-slate-500">方向</div><WealthStatusBadge type="direction" :value="selectedLedger.direction" /></div>
          <div><div class="text-xs text-slate-500">金额</div><div class="font-medium">¥{{ formatMoney(selectedLedger.amount) }}</div></div>
          <div><div class="text-xs text-slate-500">备注</div><div>{{ selectedLedger.remark || '-' }}</div></div>
        </section>
        <section class="rounded-lg border border-slate-200">
          <div class="border-b border-slate-200 px-4 py-3"><h4 class="text-sm font-semibold">批次明细</h4></div>
          <div class="data-table-shell border-0 shadow-none">
            <div class="data-table-scroll">
              <table class="data-table text-sm">
                <thead class="data-table-head"><tr><th class="data-table-th text-left">批次ID</th><th class="data-table-th text-right">金额</th><th class="data-table-th text-right">批次本金前后</th></tr></thead>
                <tbody>
                  <tr v-if="ledgerDetails.length === 0"><td colspan="3" class="data-table-empty">暂无明细</td></tr>
                  <tr v-for="item in ledgerDetails" v-else :key="item.id" class="data-table-row"><td class="data-table-td">{{ item.batchId }}</td><td class="data-table-td is-right">¥{{ formatMoney(item.amount) }}</td><td class="data-table-td is-right">¥{{ formatMoney(item.beforeBatchPrincipal) }} → ¥{{ formatMoney(item.afterBatchPrincipal) }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </WealthDetailDrawer>

    <WealthDetailDrawer v-model="batchLedgerVisible" title="批次流水" :subtitle="selectedBatchLedgerBatch?.batchNo">
      <div class="space-y-4">
        <section class="rounded-lg border border-slate-200 bg-white">
          <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <h4 class="text-sm font-semibold text-slate-900">批次资金轨迹</h4>
              <p class="mt-0.5 text-xs text-slate-500">按当前批次关联流水展示，减资、撤资等多批次流水以明细表口径匹配。</p>
            </div>
            <button type="button" class="btn-secondary" :disabled="batchLedgersLoading" @click="loadBatchLedgers">刷新</button>
          </div>
          <div class="data-table-shell border-0 shadow-none">
            <div class="data-table-scroll">
              <table class="data-table text-sm">
                <thead class="data-table-head">
                  <tr>
                    <th class="data-table-th text-left">流水</th>
                    <th class="data-table-th text-left">类型</th>
                    <th class="data-table-th text-left">方向</th>
                    <th class="data-table-th text-right">金额</th>
                    <th class="data-table-th text-left">时间</th>
                    <th class="data-table-th text-center">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="batchLedgersLoading"><td colspan="6" class="data-table-empty">加载中...</td></tr>
                  <tr v-else-if="batchLedgers.length === 0"><td colspan="6" class="data-table-empty">暂无批次流水</td></tr>
                  <tr v-for="row in batchLedgers" v-else :key="row.id" class="data-table-row">
                    <td class="data-table-td"><div class="font-mono text-xs">{{ row.ledgerNo }}</div><div class="font-mono text-xs text-slate-500">{{ row.accountingSerialNo }}</div></td>
                    <td class="data-table-td"><WealthStatusBadge type="operation" :value="row.operationType" /></td>
                    <td class="data-table-td"><WealthStatusBadge type="direction" :value="row.direction" /></td>
                    <td class="data-table-td is-right font-medium">¥{{ formatMoney(row.amount) }}</td>
                    <td class="data-table-td text-xs">{{ formatDateTime(row.createTime) }}</td>
                    <td class="data-table-td is-center">
                      <button class="table-action-button is-primary" title="明细" aria-label="明细" @click="openLedgerDetail(row)">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <AppPagination
            v-if="batchLedgerPagination.total > 0"
            class="pagination-bridge px-4 pb-4"
            :pagination="batchLedgerPagination"
            :page-sizes="PAGE_SIZE_OPTIONS"
            @page-change="handleBatchLedgerPageChange"
            @size-change="handleBatchLedgerSizeChange"
          />
        </section>
      </div>
    </WealthDetailDrawer>

    <WealthDetailDrawer v-model="applyDetailVisible" title="资金申请详情" :subtitle="selectedApply?.applyNo">
      <div v-if="selectedApply" class="space-y-5">
        <div class="flex justify-end">
          <button
            v-if="selectedApply.approvalInstanceId"
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            @click="openApprovalDetail(selectedApply)"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            查看审批详情
          </button>
        </div>
        <section class="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-4">
          <div><div class="text-xs text-slate-500">操作类型</div><WealthStatusBadge type="operation" :value="selectedApply.operationType" /></div>
          <div><div class="text-xs text-slate-500">状态</div><WealthStatusBadge type="apply" :value="selectedApply.status" /></div>
          <div><div class="text-xs text-slate-500">申请金额</div><div class="font-medium">¥{{ formatMoney(selectedApply.amount) }}</div></div>
          <div><div class="text-xs text-slate-500">续期本金</div><div class="font-medium">¥{{ formatMoney(selectedApply.rolloverPrincipalAmount) }}</div></div>
          <div><div class="text-xs text-slate-500">合同ID</div><div>{{ selectedApply.contractId || '-' }}</div></div>
          <div><div class="text-xs text-slate-500">审批实例</div><div>{{ selectedApply.approvalInstanceId || '-' }}</div></div>
          <div><div class="text-xs text-slate-500">申请人</div><div>{{ selectedApply.applicantName || selectedApply.applicantUserId || '-' }}</div></div>
          <div><div class="text-xs text-slate-500">失败原因</div><div class="text-red-600">{{ selectedApply.accountFailedReason || selectedApply.approvalStartFailedReason || '-' }}</div></div>
        </section>
        <section class="rounded-lg border border-slate-200">
          <div class="border-b border-slate-200 px-4 py-3"><h4 class="text-sm font-semibold text-slate-900">批次明细</h4></div>
          <div class="data-table-shell border-0 shadow-none">
            <div class="data-table-scroll">
              <table class="data-table text-sm">
                <thead class="data-table-head"><tr><th class="data-table-th text-left">批次</th><th class="data-table-th text-right">金额</th><th class="data-table-th text-right">提交前本金</th><th class="data-table-th text-left">起止日期</th></tr></thead>
                <tbody>
                  <tr v-if="applyDetails.length === 0"><td colspan="4" class="data-table-empty">暂无明细</td></tr>
                  <tr v-for="item in applyDetails" v-else :key="item.id" class="data-table-row">
                    <td class="data-table-td"><div class="font-mono text-xs">{{ item.batchNoSnapshot || item.batchId }}</div></td>
                    <td class="data-table-td is-right">¥{{ formatMoney(item.amount) }}</td>
                    <td class="data-table-td is-right">¥{{ formatMoney(item.beforeBatchPrincipalSnapshot) }}</td>
                    <td class="data-table-td">{{ formatDate(item.valueDateSnapshot) }} 至 {{ formatDate(item.maturityDateSnapshot) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <WealthAttachmentPanel title="申请附件" biz-type="OPERATION_APPLY" :biz-id="selectedApply.id" :can-manage="false" />
      </div>
    </WealthDetailDrawer>

    <ApprovalDetailModal
      v-model="showApprovalDetailModal"
      :instance-id="selectedApprovalInstanceId"
      :type="'wealth'"
      title="审批详情"
    />
  </ListPageShell>
</template>

<script setup>
import { computed, defineComponent, h, reactive, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import UserSelector from '@/components/UserSelector/index.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import DeptSelector from '@/views/system/components/DeptSelector/index.vue'
import { getCustomer } from '@/api/customer'
import {
  disableWealthCustomer,
  enableWealthCustomer,
  getWealthCustomerAccount,
  getWealthCustomerContractPage,
  getWealthCustomerPage,
  getWealthBatchLedgerPage,
  getWealthFundLedgerBatchDetails,
  getWealthFundLedgerPage,
  getWealthOperationApplyBatchDetails,
  getWealthOperationApplyPage,
  getWealthStatisticsOverview
} from '@/api/wealth'
import WealthAttachmentPanel from '../components/WealthAttachmentPanel.vue'
import WealthBatchPanel from '../components/WealthBatchPanel.vue'
import WealthCustomerFormModal from '../components/WealthCustomerFormModal.vue'
import WealthDetailDrawer from '../components/WealthDetailDrawer.vue'
import WealthMetricCard from '../components/WealthMetricCard.vue'
import WealthOperationApplyModal from '../components/WealthOperationApplyModal.vue'
import WealthStatusBadge from '../components/WealthStatusBadge.vue'
import {
  applyStatusOptions,
  cleanPayload,
  customerStatusOptions,
  findOptionLabel,
  formatDate,
  formatDateTime,
  formatMoney,
  idTypeOptions,
  maskIdNo,
  maskPhone,
  operationTypeOptions,
  PAGE_SIZE_OPTIONS,
  pagePayload,
  riskLevelOptions,
  syncPageResult
} from '../utils'

const WEALTH_CUSTOMER_AUTO_REFRESH_INTERVAL_MS = 15000

const FieldBlock = defineComponent({
  name: 'FieldBlock',
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: '-' }
  },
  setup(props) {
    return () => h('div', [
      h('div', { class: 'text-xs text-slate-500' }, props.label),
      h('div', { class: 'mt-1 text-sm text-slate-700' }, props.value || '-')
    ])
  }
})

const filters = reactive({ keyword: '', status: '', riskLevel: '', ownerUserId: null, ownerDept: null })
const pagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const rows = ref([])
const loading = ref(false)
const overview = ref({})
const statisticsLoading = ref(false)
const lastAutoRefreshAt = ref(0)

const formVisible = ref(false)
const editingCustomer = ref(null)
const detailVisible = ref(false)
const selectedCustomer = ref(null)
const linkedCrmCustomer = ref(null)
const activeTab = ref('overview')
const detailTabs = [
  { value: 'overview', label: '概览' },
  { value: 'contracts', label: '合同' },
  { value: 'ledgers', label: '流水' },
  { value: 'applies', label: '申请记录' }
]

const account = ref({})

const contracts = ref([])
const contractsLoading = ref(false)
const contractPagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const expandedContractId = ref(null)
const contractOngoingOperationMap = ref({})
const batchOngoingOperationMap = ref({})

const ledgers = ref([])
const ledgersLoading = ref(false)
const ledgerFilters = reactive({ operationType: '' })
const ledgerPagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const ledgerDetailVisible = ref(false)
const selectedLedger = ref(null)
const ledgerDetails = ref([])
const batchLedgerVisible = ref(false)
const selectedBatchLedgerBatch = ref(null)
const batchLedgers = ref([])
const batchLedgersLoading = ref(false)
const batchLedgerPagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })

const applies = ref([])
const appliesLoading = ref(false)
const applyFilters = reactive({ status: '' })
const applyPagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const applyDetailVisible = ref(false)
const selectedApply = ref(null)
const applyDetails = ref([])
const showApprovalDetailModal = ref(false)
const selectedApprovalInstanceId = ref(null)

const applyVisible = ref(false)
const applyType = ref('INVEST')
const applyPreset = ref({})
const ongoingApplyStatuses = ['SUBMITTING', 'APPROVING', 'ACCOUNT_FAILED']

const linkedCrmCustomerDisplay = computed(() => {
  const linkedId = selectedCustomer.value?.linkedCrmCustomerId
  if (!linkedId) return '-'
  const customer = linkedCrmCustomer.value
  if (customer?.id === linkedId) {
    return formatCrmCustomerLabel(customer)
  }
  return `CRM客户 ${linkedId}`
})

function extractId(value) {
  if (Array.isArray(value)) {
    return extractId(value[0])
  }
  if (value && typeof value === 'object') {
    return value.id
  }
  return value || null
}

function customerQueryPayload() {
  return cleanPayload({
    keyword: filters.keyword,
    status: filters.status,
    riskLevel: filters.riskLevel,
    ownerUserId: extractId(filters.ownerUserId),
    ownerDeptId: extractId(filters.ownerDept)
  })
}

async function loadStatistics() {
  statisticsLoading.value = true
  try {
    overview.value = await getWealthStatisticsOverview() || {}
  } finally {
    statisticsLoading.value = false
  }
}

async function loadCustomers() {
  loading.value = true
  try {
    const result = await getWealthCustomerPage(pagePayload(pagination, customerQueryPayload()))
    rows.value = result?.records || []
    syncPageResult(pagination, result)
  } finally {
    loading.value = false
  }
}

async function refreshCustomerListAndStatistics() {
  await Promise.all([
    loadCustomers(),
    loadStatistics()
  ])
}

async function refreshWealthCustomerSnapshot() {
  await loadStatistics()

  if (!detailVisible.value || !selectedCustomer.value?.id) return

  await Promise.all([
    loadAccount(selectedCustomer.value.id),
    loadContracts()
  ])

  if (activeTab.value === 'ledgers') {
    await loadLedgers()
  }
  if (activeTab.value === 'applies') {
    await loadApplies()
  }
}

function handleCustomerPageAutoRefresh() {
  if (document.visibilityState !== 'visible') return
  const now = Date.now()
  if (now - lastAutoRefreshAt.value < WEALTH_CUSTOMER_AUTO_REFRESH_INTERVAL_MS) return
  lastAutoRefreshAt.value = now
  refreshWealthCustomerSnapshot()
}

function handleSearch() {
  pagination.current = 1
  pagination.page = 1
  loadCustomers()
}

function handleReset() {
  Object.assign(filters, { keyword: '', status: '', riskLevel: '', ownerUserId: null, ownerDept: null })
  handleSearch()
}

function handlePageChange(page) {
  pagination.current = page
  pagination.page = page
  loadCustomers()
}

function handleSizeChange(size) {
  pagination.size = size
  handleSearch()
}

function openCreate() {
  editingCustomer.value = null
  formVisible.value = true
}

function openEdit(row) {
  editingCustomer.value = { ...row }
  formVisible.value = true
}

async function changeStatus(row, action) {
  try {
    await ElMessageBox.confirm(`确认${action === 'disable' ? '停用' : '启用'}客户“${row.name}”吗？`, '状态变更', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
    if (action === 'disable') {
      await disableWealthCustomer(row.id, {})
      ElMessage.success('客户已停用')
    } else {
      await enableWealthCustomer(row.id, {})
      ElMessage.success('客户已启用')
    }
    loadCustomers()
    loadStatistics()
  } catch (error) {
    if (error !== 'cancel') console.error(error)
  }
}

async function openDetail(row) {
  selectedCustomer.value = row
  detailVisible.value = true
  activeTab.value = 'overview'
  account.value = {}
  linkedCrmCustomer.value = null
  contracts.value = []
  ledgers.value = []
  applies.value = []
  expandedContractId.value = null
  resetDetailPagination()
  await Promise.all([
    loadAccount(row.id),
    loadContracts(),
    loadLinkedCrmCustomer(row)
  ])
}

function resetDetailPagination() {
  Object.assign(contractPagination, { current: 1, page: 1, size: 10, total: 0, pages: 1 })
  Object.assign(ledgerPagination, { current: 1, page: 1, size: 10, total: 0, pages: 1 })
  Object.assign(applyPagination, { current: 1, page: 1, size: 10, total: 0, pages: 1 })
  Object.assign(ledgerFilters, { operationType: '' })
  Object.assign(applyFilters, { status: '' })
}

async function loadAccount(customerId) {
  account.value = await getWealthCustomerAccount(customerId) || {}
}

function formatCrmCustomerLabel(customer = {}) {
  return [customer.name, customer.phone || customer.mobile || customer.customerNo].filter(Boolean).join(' / ') || `CRM客户 ${customer.id}`
}

async function loadLinkedCrmCustomer(row) {
  linkedCrmCustomer.value = null
  if (!row?.linkedCrmCustomerId) return

  linkedCrmCustomer.value = {
    id: row.linkedCrmCustomerId,
    name: row.linkedCrmCustomerName || `CRM客户 ${row.linkedCrmCustomerId}`,
    phone: row.linkedCrmCustomerMobile || ''
  }

  try {
    const customer = await getCustomer(row.linkedCrmCustomerId)
    if (customer?.id === row.linkedCrmCustomerId) {
      linkedCrmCustomer.value = customer
    }
  } catch (error) {
    console.error('load linked CRM customer failed:', error)
  }
}

async function loadContracts() {
  if (!selectedCustomer.value?.id) return
  contractsLoading.value = true
  try {
    const result = await getWealthCustomerContractPage(selectedCustomer.value.id, pagePayload(contractPagination))
    contracts.value = result?.records || []
    syncPageResult(contractPagination, result)
    await loadOngoingOperationsForVisibleContracts()
  } finally {
    contractsLoading.value = false
  }
}

function handleContractPageChange(page) {
  contractPagination.current = page
  contractPagination.page = page
  loadContracts()
}

function handleContractSizeChange(size) {
  contractPagination.size = size
  contractPagination.current = 1
  contractPagination.page = 1
  loadContracts()
}

function toggleContract(contract) {
  if (expandedContractId.value === contract.id) {
    expandedContractId.value = null
    return
  }
  expandedContractId.value = contract.id
  loadOngoingOperationsForContract(contract.id)
}

async function loadLedgers() {
  if (!selectedCustomer.value?.id) return
  ledgersLoading.value = true
  try {
    const result = await getWealthFundLedgerPage(pagePayload(ledgerPagination, cleanPayload({
      wealthCustomerId: selectedCustomer.value.id,
      operationType: ledgerFilters.operationType
    })))
    ledgers.value = result?.records || []
    syncPageResult(ledgerPagination, result)
  } finally {
    ledgersLoading.value = false
  }
}

function handleLedgerPageChange(page) {
  ledgerPagination.current = page
  ledgerPagination.page = page
  loadLedgers()
}

function handleLedgerSizeChange(size) {
  ledgerPagination.size = size
  ledgerPagination.current = 1
  ledgerPagination.page = 1
  loadLedgers()
}

async function openLedgerDetail(row) {
  selectedLedger.value = row
  batchLedgerVisible.value = false
  ledgerDetailVisible.value = true
  ledgerDetails.value = await getWealthFundLedgerBatchDetails(row.id)
}

async function openBatchLedgerDetail(batch) {
  selectedBatchLedgerBatch.value = batch
  batchLedgerVisible.value = true
  Object.assign(batchLedgerPagination, { current: 1, page: 1, size: 10, total: 0, pages: 1 })
  await loadBatchLedgers(batch)
}

async function loadBatchLedgers(batch = selectedBatchLedgerBatch.value) {
  if (!batch?.id) return
  batchLedgersLoading.value = true
  try {
    const result = await getWealthBatchLedgerPage(batch.id, pagePayload(batchLedgerPagination))
    batchLedgers.value = result?.records || []
    syncPageResult(batchLedgerPagination, result)
  } finally {
    batchLedgersLoading.value = false
  }
}

function handleBatchLedgerPageChange(page) {
  batchLedgerPagination.current = page
  batchLedgerPagination.page = page
  loadBatchLedgers()
}

function handleBatchLedgerSizeChange(size) {
  batchLedgerPagination.size = size
  batchLedgerPagination.current = 1
  batchLedgerPagination.page = 1
  loadBatchLedgers()
}

async function loadApplies() {
  if (!selectedCustomer.value?.id) return
  appliesLoading.value = true
  try {
    const result = await getWealthOperationApplyPage(pagePayload(applyPagination, cleanPayload({
      wealthCustomerId: selectedCustomer.value.id,
      status: applyFilters.status
    })))
    applies.value = result?.records || []
    syncPageResult(applyPagination, result)
  } finally {
    appliesLoading.value = false
  }
}

function handleApplyPageChange(page) {
  applyPagination.current = page
  applyPagination.page = page
  loadApplies()
}

function handleApplySizeChange(size) {
  applyPagination.size = size
  applyPagination.current = 1
  applyPagination.page = 1
  loadApplies()
}

async function openApplyDetail(row) {
  selectedApply.value = row
  applyDetailVisible.value = true
  applyDetails.value = await getWealthOperationApplyBatchDetails(row.id)
}

function openApprovalDetail(row) {
  if (!row?.approvalInstanceId) {
    ElMessage.warning('暂无审批实例')
    return
  }
  selectedApprovalInstanceId.value = row.approvalInstanceId
  showApprovalDetailModal.value = true
}

function openOngoingOperationDetail(operation) {
  if (operation?.apply) {
    openApplyDetail(operation.apply)
  }
}

function openOngoingOperationApprovalDetail(operation) {
  if (operation?.apply) {
    openApprovalDetail(operation.apply)
  }
}

function loadOngoingOperationsForVisibleContracts() {
  return Promise.all(contracts.value.map(contract => loadOngoingOperationsForContract(contract.id)))
}

async function loadOngoingOperationsForContract(contractId) {
  const results = await Promise.all(ongoingApplyStatuses.map(status => getWealthOperationApplyPage({
    page: 1,
    size: 100,
    contractId,
    status
  })))
  const applies = results.flatMap(result => result?.records || [])
  const contractOperations = []
  const nextBatchMap = {}

  await Promise.all(applies.map(async apply => {
    const summary = toOngoingOperationSummary(apply)
    contractOperations.push(summary)
    let details = []
    try {
      details = await getWealthOperationApplyBatchDetails(apply.id)
    } catch (error) {
      console.error(error)
    }
    if (details.length === 0 && apply.targetBatchId) {
      details = [{ batchId: apply.targetBatchId, amount: apply.rolloverPrincipalAmount || apply.amount }]
    }
    details.forEach(detail => {
      if (!detail.batchId) return
      nextBatchMap[detail.batchId] = nextBatchMap[detail.batchId] || []
      nextBatchMap[detail.batchId].push(toOngoingOperationSummary(apply, detail))
    })
  }))

  contractOngoingOperationMap.value = {
    ...contractOngoingOperationMap.value,
    [contractId]: contractOperations
  }
  batchOngoingOperationMap.value = nextBatchMap
}

function toOngoingOperationSummary(apply, detail = null) {
  return {
    apply,
    applyId: apply.id,
    applyNo: apply.applyNo,
    batchId: detail?.batchId || apply.targetBatchId,
    operationType: apply.operationType,
    operationTypeLabel: findOptionLabel(operationTypeOptions, apply.operationType),
    status: apply.status,
    statusLabel: findOptionLabel(applyStatusOptions, apply.status),
    amount: detail?.amount || apply.rolloverPrincipalAmount || apply.amount,
    applyTime: apply.applyTime
  }
}

function formatOngoingOperationSummary(operation = {}) {
  const amount = operation.amount ? ` ¥${formatMoney(operation.amount)}` : ''
  return `${operation.operationTypeLabel || operation.operationType || '操作'} ${operation.statusLabel || operation.status || ''}${amount} · ${operation.applyNo || '-'}`
}

function openInvest(customer) {
  applyType.value = 'INVEST'
  applyPreset.value = { wealthCustomerId: customer.id, customerName: customer.name }
  applyVisible.value = true
}

function openContractApply(type, contract) {
  applyType.value = type
  applyPreset.value = {
    contractId: contract.id,
    contractName: contract.contractName,
    contractNo: contract.contractNo,
    wealthCustomerId: contract.wealthCustomerId
  }
  applyVisible.value = true
}

function openRollover(contract, batch) {
  applyType.value = 'ROLLOVER'
  applyPreset.value = {
    contractId: contract.id,
    contractName: contract.contractName,
    contractNo: contract.contractNo,
    wealthCustomerId: contract.wealthCustomerId,
    batchId: batch.id,
    batchNo: batch.batchNo
  }
  applyVisible.value = true
}

function afterCustomerSaved() {
  loadCustomers()
  loadStatistics()
}

async function afterApplySubmitted() {
  loadStatistics()
  if (selectedCustomer.value?.id) {
    await Promise.all([
      loadAccount(selectedCustomer.value.id),
      loadContracts(),
      loadApplies()
    ])
    if (expandedContractId.value) {
      await loadOngoingOperationsForContract(expandedContractId.value)
    }
  }
}

watch(activeTab, value => {
  if (value === 'contracts' && contracts.value.length === 0) loadContracts()
  if (value === 'ledgers' && ledgers.value.length === 0) loadLedgers()
  if (value === 'applies' && applies.value.length === 0) loadApplies()
})

onMounted(() => {
  loadCustomers()
  loadStatistics()
  document.addEventListener('visibilitychange', handleCustomerPageAutoRefresh)
  window.addEventListener('focus', handleCustomerPageAutoRefresh)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleCustomerPageAutoRefresh)
  window.removeEventListener('focus', handleCustomerPageAutoRefresh)
})
</script>

<style scoped>
.selector-compact :deep(.dept-wrapper) {
  min-height: 32px;
  max-height: 38px;
  overflow: hidden;
  padding: 3px 8px;
}
</style>
